import React, { PureComponent } from "react";

import { connect } from "react-redux";
import { ApolloProvider } from "@apollo/client";
import client from "client";

import { logoutRequest } from "./../../actions";
import { NavLink } from "react-router-dom";
import clsx from "clsx";
import Uri from "jsuri";
import {
  shouldDisplayProject,
  PROJECTS_WITHOUT_BOARDS_WHITELIST,
} from "../../config/projectWhitelist";

import MobileNav from "./MobileNav";
import InsightsLatestList from "../InsightsLatest/InsightsLatestList";

import {
  AppContext,
  getCurrentProjectFromUrl,
} from "../../contexts/AppContextProvider";

import NavTenantsProjects from "./NavTenantsProjects.js";
import VersionSwitcher from "./VersionSwitcher.js";

import Logo from "./../../i/axe.svg";

/**
 * Renders the Global Navigation bar which loads the Projects and Boards
 */
class Nav extends PureComponent {
  static contextType = AppContext;

  constructor(props) {
    super(props);

    this.state = {
      boards_visible: false,
      properties_visible: false,
      show_insights_portal: false,
      insights_url: null,
      settings_nav_visible: false,
      isInDevelopment: localStorage.configVersion === "development",
    };
  }

  toggleWindowPortal() {
    this.setState((state) => ({
      ...state,
      showWindowPortal: !state.showWindowPortal,
    }));
  }

  toggleInsightsPortal = (insights_url) => {
    this.setState((state) => ({
      ...state,
      show_insights_portal: !state.show_insights_portal,
      insights_url: insights_url,
    }));
  };

  closeInsightsPortal = () => {
    this.setState({ show_insights_portal: false });
  };

  handleLogout = (e) => {
    const { dispatch } = this.props;
    dispatch(logoutRequest());
  };

  activeProjectTitle = () => {
    const { currentProject } = this.context.state;
    if (currentProject.project) {
      const { project, data } = currentProject;
      const p = data.projects.find((p) => p.slug === project);
      const logo = require(`./../../i/${p.logo}`);
      return (
        <>
          {p.title}
          <img src={logo || ""} alt={p.title} className="nav-dropdown__icon" />
        </>
      );
    }
  };

  activeBoardTitle = () => {
    const { boardObj } = this.context.state.currentProject;
    if (!boardObj) return null;
    return boardObj.title;
  };

  renderProjects = () => {
    const allProjects = this.context.state.currentProject.data.projects;
    let projects = [];
    const currentVersion = localStorage.configVersion || "production";

    // Filter projects that should be displayed
    projects = allProjects
      .filter((p) => {
        // For whitelisted projects in production, include them
        const projectId = parseInt(p.id, 10);
        const isWhitelisted =
          PROJECTS_WITHOUT_BOARDS_WHITELIST.includes(projectId);
        const isProduction = currentVersion !== "development";

        if (isWhitelisted && isProduction) {
          return true; // Include whitelisted projects in production
        }

        // For other projects, just check if they should be displayed
        const shouldInclude = shouldDisplayProject(p, currentVersion);
        return shouldInclude;
      })
      .sort((a, b) => a.position - b.position);

    return projects.map((project, key) => (
      <div key={project.title} className="nav-dropdown__item">
        <NavLink
          key={key}
          className="nav-dropdown__link dropdown__item--sm"
          to={`/${project.slug}`}
          activeClassName="nav-dropdown__item--is-on"
          onClick={() => this.onSelect(key, "properties_visible")}
        >
          {project.title}
        </NavLink>
        <img
          src={(() => {
            let logo;
            try {
              logo = require(`./../../i/${project.logo}`);
            } finally {
              return logo;
            }
          })()}
          alt={project.title}
          className="nav-dropdown__icon"
        />
      </div>
    ));
  };

  renderBoards = (showArchivedOnly = false) => {
    const { project, data } = this.context.state.currentProject;

    if (!project) return null;

    const projectData = data.projects.find((p) => p.slug === project);
    if (!projectData || !projectData.boards || projectData.boards.length === 0)
      return null;

    const boards = projectData.boards;
    const boards_sorted = boards.sort((a, b) => a.position - b.position);

    const filter = !showArchivedOnly
      ? (page) => page.slug !== "insights" && !page.archived
      : (page) => page.slug !== "insights" && page.archived;

    const filteredOptions = boards_sorted.filter(filter);
    return (
      <>
        {(!showArchivedOnly ||
          (showArchivedOnly && filteredOptions.length > 0)) && (
          <h3
            className={
              "nav-dropdown__title" + (showArchivedOnly ? " mt-3" : "")
            }
          >
            {!showArchivedOnly ? "Select Board" : "Archived"}
          </h3>
        )}

        {filteredOptions.map((board, key) => (
          <NavLink
            key={key}
            className="nav-dropdown__item nav-dropdown__item--sm"
            to={`/${project}/${board.slug}`}
            activeClassName="nav-dropdown__item--is-on"
            onClick={() => this.onSelect(key, "boards_visible")}
          >
            {board.title}
          </NavLink>
        ))}
      </>
    );
  };

  showDropdown = (dropdown, value = true) => {
    this.setState({
      [dropdown]: value,
    });
  };

  onSelect(i, dropdown) {
    this.setState({
      [dropdown]: false,
    });

    // If this is a project selection, set the defaultProjectID
    if (dropdown === "properties_visible") {
      const allProjects = this.context.state.currentProject.data.projects;

      const projects = allProjects
        .filter((p) => p.version === localStorage.configVersion || "production")
        .filter((p) =>
          shouldDisplayProject(p, localStorage.configVersion || "production")
        )
        .sort((a, b) => a.position - b.position);

      const selectedProject = projects[i];
      if (selectedProject && selectedProject.id) {
        localStorage.setItem("defaultProjectID", selectedProject.id);
        this.context.dispatch({
          type: "APP-SET-PROJECT_ID",
          projectID: selectedProject.id,
        });
      }
    }
  }

  componentDidMount = () => {
    document.addEventListener("click", this.onClickOutside);
  };

  componentWillUnmount = () => {
    document.removeEventListener("click", this.onClickOutside);
  };

  setWrapperBoards = (node) => {
    this.boardsRef = node;
  };

  setPropertiesBoards = (node) => {
    this.propertiesRef = node;
  };

  setSettingsNav = (node) => (this.settingsNavRef = node);

  onClickOutside = (event) => {
    if (this.boardsRef && !this.boardsRef.contains(event.target)) {
      this.setState({ boards_visible: false });
    }
    if (this.propertiesRef && !this.propertiesRef.contains(event.target)) {
      this.setState({ properties_visible: false });
    }
    if (this.settingsNavRef && !this.settingsNavRef.contains(event.target)) {
      this.setState({ settings_nav_visible: false });
    }
  };

  getUserName = () => localStorage.user;

  // When on query builder refresh the entire page ("reset")
  renderModuleLink = (module, name) => {
    const { currentProject } = this.context.state;
    const isModule =
      window.location.pathname === `/${currentProject.project}/${module}`;

    return (
      <>
        <span className="nav__item-pipe" />
        <span className="nav-dropdown">
          <div className="nav-dropdown__parent nav-dropdown__parent--properties--query-state">
            <NavLink
              to={`/${currentProject.project}/${module}`}
              onClick={() =>
                isModule
                  ? window.location.replace(
                      `/${currentProject.project}/${module}`
                    )
                  : null
              }
            >
              {name}
            </NavLink>
          </div>
        </span>
      </>
    );
  };

  render() {
    const { currentProject } = this.context.state;
    if (!currentProject) return null;
    const { project, data } = currentProject;

    // get the current project based in the url
    const currentUrlProject = getCurrentProjectFromUrl();

    const current_project =
      (data && data.projects.find((p) => p.slug === project)) || {};

    let insights_url =
      current_project.nav &&
      current_project.nav.props &&
      current_project.nav.props.insights_url;

    // checks whether there's a threshold prop from the browser URL
    const thresholdParam = new Uri(window.location.search).getQueryParamValue(
      "threshold"
    );

    // if so then replace it by the one from the insights feed url
    if (thresholdParam) {
      // creates a temp Url based on the `insights_url`
      let insightsConfigURL = new Uri(insights_url);
      insightsConfigURL = insightsConfigURL.replaceQueryParam(
        "threshold",
        thresholdParam
      );
      // set the new url for insight
      insights_url = insightsConfigURL.toString();
    }

    const query_link = current_project?.nav?.props?.queryBuilder;

    const mix_link = current_project?.nav?.props?.mix;

    // Temprorary: notification bar for missing data
    // const flm_projects = [2, 3, 4, 5];
    // const is_flm = flm_projects.includes(parseInt(project_id));

    // NBC News - 38
    // NBC Bravo - 12
    // NBC USA Network - 13
    // NBC Sprout - 22
    // NBC Sports - 8
    // NBC RNS - 21
    // NBC Golf - 11
    // NBC E! - 15
    // Fandango - 16
    // Nextstar - 90
    // Hayu - 84
    // Sky - 106, 72, 38, 74, 73, 106
    // Telemundo 9
    // NBC Sports Edge - 95
    // G4 - 140

    const project_insights_ids = [
      2, 7, 54, 68, 84, 86, 90, 106, 72, 38, 74, 73, 106, 9, 95, 115, 116, 117,
      118, 109, 113, 114, 140, 144, 145, 146, 147, 149, 152, 157, 164,
    ];
    const project_insight_id = current_project.id;
    const show_project_insights = project_insights_ids.includes(
      parseInt(project_insight_id)
    );

    const has_multiple_tenants = data.tenants.length > 1;

    // Check if current project has boards
    const hasBoards =
      current_project.boards && current_project.boards.length > 0;

    return (
      <ApolloProvider client={client}>
        <div className="nav-container-wrapper">
          <div className="container-fluid">
            {this.state.show_insights_portal && show_project_insights && (
              <InsightsLatestList
                url={this.state.insights_url}
                property={project}
                closeInsightsPortal={this.closeInsightsPortal}
              />
            )}

            <nav className="pt-4 pl-2 mb-2 row">
              <div className="col-auto mr-2">
                <NavLink className="nav__home" to="/">
                  <img src={Logo} alt="Pickaxe" className="nav__logo" />
                </NavLink>
              </div>

              <div className="col-8 nav__items d-none d-md-block">
                {(() => {
                  // Get projects that should be displayed (same logic as renderProjects)
                  const currentVersion =
                    localStorage.configVersion || "production";
                  const projectsWithBoards =
                    currentProject.data.projects.filter((p) => {
                      // For whitelisted projects in production, include them
                      const projectId = parseInt(p.id, 10);
                      const isWhitelisted =
                        PROJECTS_WITHOUT_BOARDS_WHITELIST.includes(projectId);
                      const isProduction = currentVersion !== "development";

                      if (isWhitelisted && isProduction) {
                        return true; // Include whitelisted projects in production
                      }

                      // For other projects, just check if they should be displayed
                      const shouldInclude = shouldDisplayProject(
                        p,
                        currentVersion
                      );

                      return shouldInclude;
                    });

                  return projectsWithBoards.length > 1 ? (
                    <span
                      className="nav-dropdown"
                      ref={this.setPropertiesBoards}
                    >
                      <div
                        className="nav-dropdown__parent"
                        onClick={() => this.showDropdown("properties_visible")}
                      >
                        {this.activeProjectTitle()}{" "}
                        <span className="text-gray">&#9662;</span>
                      </div>
                      <div
                        className={
                          this.state.properties_visible
                            ? "nav-dropdown__options nav-dropdown__options--is-visible"
                            : "nav-dropdown__options"
                        }
                      >
                        <div className="nav-dropdown__options-container">
                          {this.renderProjects()}
                        </div>
                      </div>
                    </span>
                  ) : (
                    <a href="/" className="nav-dropdown__parent">
                      {this.activeProjectTitle()}
                    </a>
                  );
                })()}
                {/* Only show boards dropdown and pipe if project has boards */}
                {hasBoards && (
                  <>
                    <span className="nav__item-pipe" />
                    <span className="nav-dropdown" ref={this.setWrapperBoards}>
                      <div
                        className="nav-dropdown__parent nav-dropdown__parent--properties"
                        onClick={() => this.showDropdown("boards_visible")}
                      >
                        {currentUrlProject.page === undefined ||
                        (currentProject.boardObj &&
                          currentProject.page === currentUrlProject.page) ? (
                          this.activeBoardTitle()
                        ) : (
                          <span className="nav-dropdown__parent">
                            Select Board
                          </span>
                        )}{" "}
                        <span className="text-gray">&#9662;</span>
                      </div>
                      <div
                        className={
                          this.state.boards_visible
                            ? "nav-dropdown__options p-4 nav-dropdown__options--is-visible"
                            : "nav-dropdown__options p-4"
                        }
                      >
                        <div className="nav-dropdown__options-container">
                          {data ? this.renderBoards() : null}
                          {data ? this.renderBoards(true) : null}
                        </div>
                      </div>
                    </span>
                  </>
                )}
                {/* show/hide queryBuilder according the definition in the project */}
                {(query_link || this.state.isInDevelopment) &&
                  this.renderModuleLink("query", "Query")}
                {(mix_link || this.state.isInDevelopment) &&
                  this.renderModuleLink("mix", "Mix")}
              </div>

              <div className="pr-3 ml-auto d-none d-md-flex align-items-center">
                {insights_url && show_project_insights && (
                  <button
                    className={clsx(
                      "nav__insights nav__insights--sm pr-2 pl-2 mr-4",
                      {
                        "nav__insights--on": this.state.show_insights_portal,
                      }
                    )}
                    onClick={() => this.toggleInsightsPortal(insights_url)}
                  >
                    Insights{" "}
                    {/* <span className="ml-2 nav__insights-count">4</span>   */}
                  </button>
                )}

                <span className="nav-dropdown" ref={this.setSettingsNav}>
                  <div
                    className="nav-dropdown__user-name text-capitalize"
                    onClick={() => this.showDropdown("settings_nav_visible")}
                  >
                    {this.getUserName()}
                    <span className="pl-1 text-gray">&#9662;</span>
                  </div>
                  <div
                    className={
                      this.state.settings_nav_visible
                        ? "nav-dropdown__settings nav-dropdown__settings--is-visible"
                        : "nav-dropdown__settings"
                    }
                  >
                    <div className="nav-dropdown d-flex flex-column">
                      <div className="nav-dropdown__user-name text-capitalize">
                        {this.getUserName()}
                      </div>

                      <NavLink
                        className="pt-1 nav-dropdown__item-right"
                        to="/settings/sources"
                        onClick={() => {
                          this.onSelect("settings_nav_visible");
                          this.showDropdown("settings_nav_visible", false);
                        }}
                      >
                        Sources
                      </NavLink>

                      {localStorage.roles &&
                        localStorage.roles.includes("admin") && (
                          <NavLink
                            className="pt-1 nav-dropdown__item-right"
                            to="/settings/users"
                            onClick={() => {
                              this.onSelect("settings_nav_visible");
                              this.showDropdown("settings_nav_visible", false);
                            }}
                          >
                            Users
                          </NavLink>
                        )}

                      {has_multiple_tenants && (
                        <>
                          <hr className="w-100" />
                          <NavTenantsProjects
                            tenants={data.tenants}
                            className={"nav-dropdown__item-right pt-1"}
                          />
                          <hr className="w-100" />
                        </>
                      )}
                      <button
                        className="pr-0 btn btn-link btn-logout nav-dropdown__item-right"
                        onClick={this.handleLogout}
                      >
                        Logout
                      </button>
                    </div>
                  </div>
                </span>
              </div>

              <MobileNav
                show_project_insights={show_project_insights}
                insights_url={insights_url}
                tenants={data.tenants}
                {...this.props}
              />
            </nav>
          </div>
        </div>
        {/* Display version switchers everywhere except production */}
        {window.location.host.split(".")[0] !== "dashboard" ? (
          <VersionSwitcher />
        ) : null}
      </ApolloProvider>
    );
  }
}

export default connect()(Nav);
